/**
 * PathwayModal — composable JSX-to-atom bridge + singleton renderer.
 *
 * Usage:
 *
 *   // 1. Render the singleton ONCE high in the tree (e.g. App.tsx):
 *   <PathwayModalRenderer />
 *
 *   // 2. In any component, compose the modal declaratively:
 *   <PathwayModal isOpen={open} onClose={close} stepKey={step}>
 *     <PathwayModal.Header>
 *       <PathwayModal.Title>My Title</PathwayModal.Title>
 *       <Searchbar />  {/* optional extra header content *\/}
 *     </PathwayModal.Header>
 *     <PathwayModal.Body><MyBody /></PathwayModal.Body>
 *     <PathwayModal.Footer>
 *       <PathwayModal.SecondaryButton onPress={onCancel}>Cancel</PathwayModal.SecondaryButton>
 *       <PathwayModal.PrimaryButton onPress={onSave} isDisabled={!valid}>Save</PathwayModal.PrimaryButton>
 *     </PathwayModal.Footer>
 *   </PathwayModal>
 *
 * ---------------------------------------------------------------------------
 * Slot collection — how it works
 * ---------------------------------------------------------------------------
 *
 * The marker components (Header, Body, Footer) return null — they produce no
 * DOM output. They communicate their content to the composer via a shared ref
 * that is passed through React context, AND write directly to the atom.
 *
 * Why a ref in context rather than just a bare ref?
 *
 *   slotsRef is created and owned by PathwayModalComposer. The composer can
 *   read it directly. But the marker components are not called by the composer
 *   — React renders them as fibers somewhere in the subtree, potentially
 *   inside a wrapper function component (e.g. <ChooseTemplateModalStep />) that the
 *   composer knows nothing about. The only way for those descendants to reach
 *   a value owned by the composer is through context.
 *
 *   Putting the ref *object* (not ref.current) in context is key. The ref
 *   object is a stable box — same identity for the life of the composer.
 *   When the composer resets slotsRef.current = {} at the start of each
 *   render, it replaces the *contents* of the box. Any consumer that holds
 *   the box (via context) will read the same box the composer just cleared,
 *   so there is no staleness. If we put ref.current in context instead, each
 *   consumer would receive a snapshot of the old object pointer, which becomes
 *   orphaned after the reset — writes would go into an object nobody reads.
 *
 * Why doesn't useContext trigger re-renders?
 *
 *   The context value includes the stable ref object plus registration signals
 *   (`stepKey` and `renderVersion`). React re-renders consumers when either
 *   signal changes, which forces marker components to re-register their slots
 *   even when React Compiler would otherwise memoize them.
 *
 * What triggers the useLayoutEffect in marker components?
 *
 *   The marker component rendering. All marker-component useLayoutEffects have
 *   no dep array, so they run after every commit. They render because either
 *   their parent rendered or the slot collector context changed.
 *
 * Commit-phase ordering (bottom-up):
 *
 *   React fires layout effects leaf-first. So by the time the composer's
 *   own useLayoutEffect runs, every child's useLayoutEffect has already
 *   written its slot into slotsRef.current. The composer reads a fully
 *   populated object and writes it to the atom in a single synchronous pass.
 *
 *   1. Composer renders → slotsRef.current = {}  (reset)
 *   2. React renders the full subtree (ChooseTemplateModalStep, Header, Body, …)
 *   3. Commit: Header's useLayoutEffect → writes slotsRef + patches atom
 *   4. Commit: Body's useLayoutEffect  → writes slotsRef + patches atom
 *   5. Commit: Footer's useLayoutEffect → writes slotsRef + patches atom
 *   6. Commit: Composer's useLayoutEffect → reads slotsRef.current → setModal
 *      (full-sync write; steps 3–5 ensure async re-renders also reach the atom)
 *
 * Why do marker components also write directly to the atom (steps 3–5)?
 *
 *   The composer's useLayoutEffect only fires when the composer itself
 *   re-renders. Children that re-render independently — e.g. because an async
 *   query resolved inside a modal body component — will commit new content to
 *   their marker's useLayoutEffect, but the composer stays still and never
 *   calls setModal. The direct atom patch in each marker ensures these async
 *   updates always reach PathwayModalRenderer, regardless of whether the
 *   composer re-rendered. The patch is guarded by !prev.isOpen so a closed
 *   composer never clobbers an unrelated open modal.
 *
 * ---------------------------------------------------------------------------
 * Async resize animation — how it works
 * ---------------------------------------------------------------------------
 *
 *   When body content changes height asynchronously (e.g. a query resolves
 *   and replaces a loading spinner with a list of items), PathwayModalRenderer
 *   detects the height change in a pre-paint useLayoutEffect and triggers a
 *   resize-only animation (resizeOnlyRef = true) before the browser paints.
 *
 *   Height comparison vs. reference comparison:
 *
 *     JSX children are new object references on every render, so comparing
 *     prevContent !== nextContent would fire on every re-render (every
 *     isFetching toggle, every state update) causing rapid animation cycling
 *     and header flickering. DOM offsetHeight comparison fires only when the
 *     rendered height actually changes — stable across re-renders that produce
 *     the same visual output.
 *
 *   Resize-only path (resizeOnlyRef = true):
 *
 *     • Opacity is kept at 1 for header, body, and footer — no fade flash.
 *     • Header/body render atom values directly (not displayedContent) so
 *       the current content stays visible during the height animation.
 *     • After the resize transition completes, contentHeight resets to 'auto'
 *       and the animation returns to 'idle' without a fading-in phase.
 *
 *   Full step-change path (resizeOnlyRef = false, stepKey changed):
 *
 *     • fading-out → resizing → fading-in → idle, as before.
 *     • Header/body render displayedContent during fading-out and resizing
 *       so the outgoing content is visible through the fade-out.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { Button, ButtonProps, Modal } from '@cfa/react-core';

import { cn } from '@/utils/tailwind';

import ScrollModalFooter from '@/components/ScrollModal/ScrollModalFooterCore';
import ScrollModalHeader from '@/components/ScrollModal/ScrollModalHeaderCore';

import {
  pathwayModalAtom,
  pathwayModalInitialState,
} from '@/atoms/pathwayModal';

// ---------------------------------------------------------------------------
// Marker sub-components — all return null; they are pure data carriers.
// ---------------------------------------------------------------------------

export interface TitleProps {
  /** The modal header title string. */
  children: string;
}
const Title: React.FC<TitleProps> = () => null;
Title.displayName = 'PathwayModal.Title';

export interface HeaderProps {
  /** Title (required) plus optional extra content (e.g. a Searchbar). */
  children: React.ReactNode;
  /** When true, removes default horizontal padding from the header wrapper. */
  noPadding?: boolean;
}
const Header: React.FC<HeaderProps> = ({ children, noPadding }) => {
  // Received ref — same box owned by PathwayModalComposer, obtained via
  // context. We write into it so the composer can do a full-sync setModal on
  // its own useLayoutEffect. We also patch the atom directly so async
  // re-renders of the header (e.g. a query resolving in a parent) reach
  // PathwayModalRenderer even when the composer itself did not re-render.
  const slotCollector = useContext(ModalSlotCollectorContext);
  const slotsRef = slotCollector?.slotsRef ?? null;
  const setModal = useSetAtom(pathwayModalAtom);
  const hChildren = validElements(children);
  const titleEl = hChildren.find(c => c.type === Title);
  const headerTitle = titleEl ? propsOf(titleEl, Title).children : '';
  const extra = hChildren.filter(c => c.type !== Title);
  const headerContent: React.ReactNode =
    extra.length === 0 ? undefined : extra.length === 1 ? (
      extra[0]
    ) : (
      <>{extra}</>
    );

  useLayoutEffect(() => {
    if (slotsRef) {
      slotsRef.current.headerTitle = headerTitle;
      slotsRef.current.headerContent = headerContent;
      slotsRef.current.headerNoPadding = noPadding;
      setModal(prev =>
        !prev.isOpen
          ? prev
          : { ...prev, headerTitle, headerContent, headerNoPadding: noPadding },
      );
    }
  });

  return null;
};
Header.displayName = 'PathwayModal.Header';

export interface BodyProps {
  children: React.ReactNode;
  /** When true, suppresses the 360px min-height floor applied during step transitions. Use for short static-content modals that don't need the async-resize guard. */
  noMinHeight?: boolean;
  /** Extra className applied to the inner Modal.Content wrapper. */
  className?: string;
}
const Body: React.FC<BodyProps> = ({
  children,
  noMinHeight,
  className: bodyClassName,
}) => {
  // Write-only: slot ref for full-sync composer pass + direct atom patch for
  // async child re-renders. See Header for full explanation.
  const slotCollector = useContext(ModalSlotCollectorContext);
  const slotsRef = slotCollector?.slotsRef ?? null;
  const setModal = useSetAtom(pathwayModalAtom);

  useLayoutEffect(() => {
    if (slotsRef) {
      slotsRef.current.bodyContent = children;
      slotsRef.current.bodyNoMinHeight = noMinHeight;
      slotsRef.current.bodyClassName = bodyClassName;
      setModal(prev =>
        !prev.isOpen
          ? prev
          : {
              ...prev,
              bodyContent: children,
              bodyNoMinHeight: noMinHeight,
              bodyClassName,
            },
      );
    }
  });

  return null;
};
Body.displayName = 'PathwayModal.Body';

export interface FooterProps {
  /**
   * Either PrimaryButton / SecondaryButton children for default layout,
   * or fully custom content that replaces the button pair entirely.
   */
  children: React.ReactNode;
  /**
   * Optional class name merged onto the footer wrapper (ScrollModalFooter).
   * Use to override default footer padding/spacing on a per-modal basis.
   */
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ children, className }) => {
  // Write-only: slot ref for full-sync composer pass + direct atom patch for
  // async child re-renders. See Header for full explanation.
  const slotCollector = useContext(ModalSlotCollectorContext);
  const slotsRef = slotCollector?.slotsRef ?? null;
  const fChildren = validElements(children);
  const primaryBtn = fChildren.find(c => c.type === PrimaryButton);
  const secondaryBtn = fChildren.find(c => c.type === SecondaryButton);
  const custom = fChildren.filter(
    c => c.type !== PrimaryButton && c.type !== SecondaryButton,
  );
  const footerContent: React.ReactNode =
    custom.length === 0 ? undefined : custom.length === 1 ? (
      custom[0]
    ) : (
      <>{custom}</>
    );

  const setModal = useSetAtom(pathwayModalAtom);

  useLayoutEffect(() => {
    if (!slotsRef) return;
    slotsRef.current.footerClassName = className;
    if (footerContent !== undefined) {
      slotsRef.current.footerContent = footerContent;
      setModal(prev =>
        !prev.isOpen
          ? prev
          : { ...prev, footerContent, footerClassName: className },
      );
    } else {
      if (primaryBtn) {
        const p = propsOf(primaryBtn, PrimaryButton);
        slotsRef.current.primaryButtonText = p.children;
        slotsRef.current.primaryButtonHandler = p.onPress;
        slotsRef.current.primaryButtonColor = p.color;
        slotsRef.current.primaryButtonVariant = p.variant;
        slotsRef.current.isDisabled = p.isDisabled;
        slotsRef.current.isLoading = p.isPending;
        slotsRef.current.primaryButtonTestId = p.testId;
      }
      if (secondaryBtn) {
        const s = propsOf(secondaryBtn, SecondaryButton);
        slotsRef.current.secondaryButtonText = s.children;
        slotsRef.current.secondaryButtonHandler = s.onPress;
        slotsRef.current.secondaryButtonColor = s.color;
        slotsRef.current.secondaryButtonVariant = s.variant;
        slotsRef.current.secondaryButtonDisabled = s.isDisabled;
        slotsRef.current.secondaryButtonLoading = s.isPending;
        slotsRef.current.secondaryButtonTestId = s.testId;
      }
      setModal(prev => {
        if (!prev.isOpen) return prev;
        const patch: Partial<typeof prev> = { footerClassName: className };
        if (primaryBtn) {
          const p = propsOf(primaryBtn, PrimaryButton);
          patch.primaryButtonText = p.children;
          patch.primaryButtonHandler = p.onPress;
          patch.primaryButtonColor = p.color;
          patch.primaryButtonVariant = p.variant;
          patch.isDisabled = p.isDisabled;
          patch.isLoading = p.isPending;
          patch.primaryButtonTestId = p.testId;
        }
        if (secondaryBtn) {
          const s = propsOf(secondaryBtn, SecondaryButton);
          patch.secondaryButtonText = s.children;
          patch.secondaryButtonHandler = s.onPress;
          patch.secondaryButtonColor = s.color;
          patch.secondaryButtonVariant = s.variant;
          patch.secondaryButtonDisabled = s.isDisabled;
          patch.secondaryButtonLoading = s.isPending;
          patch.secondaryButtonTestId = s.testId;
        }
        return { ...prev, ...patch };
      });
    }
  });

  return null;
};
Footer.displayName = 'PathwayModal.Footer';

export interface ModalButtonProps {
  children: React.ReactNode;
  onPress?: () => void;
  isDisabled?: boolean;
  isPending?: boolean;
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  testId?: string;
}
const PrimaryButton: React.FC<ModalButtonProps> = () => null;
PrimaryButton.displayName = 'PathwayModal.PrimaryButton';

const SecondaryButton: React.FC<ModalButtonProps> = () => null;
SecondaryButton.displayName = 'PathwayModal.SecondaryButton';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Collect only valid React elements from a ReactNode (strips strings, nulls, etc.). */
const validElements = (children: React.ReactNode): React.ReactElement[] =>
  React.Children.toArray(children).filter(React.isValidElement);

/**
 * Cast the `props` of a ReactElement to a known interface.
 * React 19 types `props` as `unknown`; passing the component as `_type` lets
 * TypeScript infer `P` from the component's props, avoiding explicit type
 * parameters at every call site while keeping `P` in two positions.
 */
// The trailing comma in <P,> disambiguates the generic from a JSX tag in .tsx files.
const propsOf = <P,>(el: React.ReactElement, _type: React.FC<P>): P =>
  el.props as P;

/**
 * Generate a unique signature from modal content.
 * Used as automatic stepKey if one isn't explicitly provided.
 */
const generateContentSignature = (
  headerTitle: string,
  bodyContent: React.ReactNode,
  primaryButtonText: React.ReactNode,
  footerContent: React.ReactNode,
): string =>
  [
    headerTitle,
    // For React elements, use a simplified representation
    bodyContent ? 'has-body' : 'no-body',
    primaryButtonText ? 'has-primary' : 'no-primary',
    footerContent ? 'has-footer' : 'no-footer',
  ]
    .filter(Boolean)
    .join('|');

// ---------------------------------------------------------------------------
// Slot collector — enables marker components at any nesting depth,
// including function-component wrappers that return a fragment of markers.
// ---------------------------------------------------------------------------

interface ModalSlots {
  headerTitle?: string;
  headerContent?: React.ReactNode;
  headerNoPadding?: boolean;
  bodyContent?: React.ReactNode;
  bodyNoMinHeight?: boolean;
  bodyClassName?: string;
  footerContent?: React.ReactNode;
  footerClassName?: string;
  primaryButtonText?: React.ReactNode;
  primaryButtonHandler?: () => void;
  primaryButtonColor?: ButtonProps['color'];
  primaryButtonVariant?: ButtonProps['variant'];
  primaryButtonTestId?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  secondaryButtonText?: React.ReactNode;
  secondaryButtonHandler?: () => void;
  secondaryButtonColor?: ButtonProps['color'];
  secondaryButtonVariant?: ButtonProps['variant'];
  secondaryButtonDisabled?: boolean;
  secondaryButtonLoading?: boolean;
  secondaryButtonTestId?: string;
}

/**
 * Private context provided by PathwayModal to its subtree.
 * Marker components (Header, Body, Footer) write their slot data into slotsRef
 * via useLayoutEffect so the composer can sync the atom after every commit.
 *
 * `stepKey` forces marker re-registration when the modal step changes.
 * `renderVersion` forces marker re-registration on every composer render,
 * including same-step renders where React Compiler could otherwise memoize a
 * marker whose props are unchanged.
 */
interface ModalSlotCollectorContextValue {
  slotsRef: React.RefObject<ModalSlots>;
  stepKey: string | undefined;
  renderVersion: number;
}

const ModalSlotCollectorContext =
  React.createContext<ModalSlotCollectorContextValue | null>(null);

// ---------------------------------------------------------------------------
// PathwayModal.Step — gate component for declarative multi-step modals
// ---------------------------------------------------------------------------

export interface StepProps {
  /**
   * The step identity key. Must match the composer's active `stepKey` prop
   * for this step's slot markers (Header/Body/Footer) to mount.
   */
  stepKey: string;
  children: React.ReactNode;
}

/**
 * Slot-gating wrapper for multi-step modals.
 *
 * Only the Step whose `stepKey` matches the composer's active `stepKey`
 * mounts its children. All other Steps return null, so only the active
 * step's Header/Body/Footer slot markers register with the atom.
 *
 * Simple single-step modals (direct Header/Body/Footer, no Step) continue
 * to work unchanged.
 *
 * @example
 * <PathwayModal isOpen={open} onClose={close} stepKey={step}>
 *   <PathwayModal.Step stepKey="operator">
 *     <PathwayModal.Header>…</PathwayModal.Header>
 *     <PathwayModal.Body>…</PathwayModal.Body>
 *     <PathwayModal.Footer>…</PathwayModal.Footer>
 *   </PathwayModal.Step>
 *   <PathwayModal.Step stepKey="name">
 *     <PathwayModal.Header>…</PathwayModal.Header>
 *     <PathwayModal.Body>…</PathwayModal.Body>
 *     <PathwayModal.Footer>…</PathwayModal.Footer>
 *   </PathwayModal.Step>
 * </PathwayModal>
 */
const Step: React.FC<StepProps> = ({ stepKey, children }) => {
  const context = useContext(ModalSlotCollectorContext);
  const activeStepKey = context?.stepKey;

  if (activeStepKey !== stepKey) return null;
  return <>{children}</>;
};
Step.displayName = 'PathwayModal.Step';

// ---------------------------------------------------------------------------
// PathwayModal (composer) — syncs atom state from marker-component children
// ---------------------------------------------------------------------------

export interface PathwayModalProps {
  isOpen: boolean;
  onClose: () => void;
  stepKey?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  overlayClassName?: string;
  overlayStyle?: React.CSSProperties;
  showCloseButton?: boolean;
  /**
   * Optional ref that will be populated with the modal body's scrollable DOM
   * element once it mounts. Use this to wire useScrollMemory or other
   * scroll-dependent behaviour to the actual scroller inside PathwayModalRenderer.
   */
  bodyRef?: React.RefObject<HTMLDivElement | null>;
  children?: React.ReactNode;
  /**
   * When true, the modal body fills 90dvh, all height animations are
   * suppressed, and opacity never changes between renders. Use for steps
   * where the body content grows/shrinks (e.g. accordions) and a
   * fixed-height layout is preferable to an animated resize.
   */
  fullHeight?: boolean;
}

// Global tracker for active modals in development mode. Exposed for testing.
// eslint-disable-next-line react-refresh/only-export-components
export const __DEV_activeModalsRef = { current: new Set<string>() };
const STEP_DURATION = 150; // Total duration of fade-out + fade-in

const PathwayModalComposer: React.FC<PathwayModalProps> = ({
  isOpen,
  onClose,
  stepKey,
  size,
  className,
  overlayClassName,
  overlayStyle,
  showCloseButton,
  bodyRef,
  children,
  fullHeight = false,
}) => {
  const modalIdRef = useRef(useId());
  // Owner ref — this composer creates the box and is the sole reader.
  // It is passed into ModalSlotCollectorContext so marker components in the
  // subtree can write into it. We pass the ref object (not ref.current) so
  // all consumers share the same mutable container regardless of resets.
  const slotsRef = useRef({} as ModalSlots);
  const setModal = useSetAtom(pathwayModalAtom);
  // Tracks the stepKey seen on the previous composer layout-effect run.
  // Used to detect step changes so we know whether to preserve previous
  // slot values (same step, memoized children) or discard them (new step).
  const prevComposerStepKeyRef = useRef<string | undefined>(undefined);
  // Incremented on every render so slot-marker components re-register
  // even when React Compiler would otherwise memoize unchanged props.
  const renderVersion = useRef(0);
  renderVersion.current += 1;

  // Clear slots at the start of each render so stale data from unmounted
  // children doesn't persist. Children re-register their slots via
  // useLayoutEffect after each commit.
  slotsRef.current = {};

  // --- Dev-mode warning for multiple competing modals ---
  useEffect(() => {
    const modalId = modalIdRef.current;

    if (process.env.NODE_ENV === 'development' && isOpen) {
      const prevSize = __DEV_activeModalsRef.current.size;
      __DEV_activeModalsRef.current.add(modalId);

      if (prevSize > 0) {
        console.warn(
          '[PathwayModal] Warning: Multiple active PathwayModal components detected. ' +
            'Only the last rendered modal will be active and animations may not work. ' +
            'Use a single <PathwayModal> with conditional body content instead. ' +
            `(${__DEV_activeModalsRef.current.size} modals currently active)`,
        );
      }
    } else if (process.env.NODE_ENV === 'development' && !isOpen) {
      __DEV_activeModalsRef.current.delete(modalId);
    }

    return () => {
      if (process.env.NODE_ENV === 'development') {
        __DEV_activeModalsRef.current.delete(modalId);
      }
    };
  }, [isOpen]);

  // After all children's layout effects have registered their slots into
  // slotsRef, sync the accumulated state to the atom. useLayoutEffect fires
  // bottom-up (children before parents), so slotsRef is fully populated by
  // the time this runs.
  //
  // No dep array is intentional — runs after every render to stay in sync.
  // Functional update on close: only clears the atom if this instance owns
  // it, preventing a closed composer from clobbering an open modal.
  //
  // Memoization-aware slot resolution:
  //   React Compiler may memoize slot-marker components (Header, Body) when
  //   their props are unchanged, preventing their useLayoutEffect from firing.
  //   slotsRef is cleared on every render, so a memoized marker leaves its
  //   slot absent from slotsRef even though its content is still valid.
  //
  //   Resolution rule — for each slot key:
  //     • If the slot was populated this cycle (key present in slotsRef): use it.
  //     • If the step key is unchanged AND this composer owns the atom: preserve
  //       the previous atom value (the memoized marker's content is still current).
  //     • If the step key changed: use undefined / '' (clear stale content from
  //       the previous step's now-unmounted markers).
  useLayoutEffect(() => {
    const id = modalIdRef.current;
    if (isOpen) {
      const slots = slotsRef.current;
      setModal(prev => {
        const isOwner = !prev._ownerId || prev._ownerId === id;
        // When a slot-marker component is memoized by React Compiler its
        // useLayoutEffect doesn't fire, so its slot key is absent from
        // slotsRef this cycle. For the auto-generated stepKey we fall back
        // to the previous atom value for any absent slot so the signature
        // stays stable across renders where only the footer changed.
        const effectiveStepKey =
          stepKey ??
          generateContentSignature(
            'headerTitle' in slots
              ? (slots.headerTitle ?? '')
              : isOwner
                ? prev.headerTitle
                : '',
            'bodyContent' in slots
              ? slots.bodyContent
              : isOwner
                ? prev.bodyContent
                : undefined,
            'primaryButtonText' in slots
              ? slots.primaryButtonText
              : isOwner
                ? prev.primaryButtonText
                : undefined,
            'footerContent' in slots
              ? slots.footerContent
              : isOwner
                ? prev.footerContent
                : undefined,
          );
        const stepChanged = prevComposerStepKeyRef.current !== effectiveStepKey;
        prevComposerStepKeyRef.current = effectiveStepKey;
        // When the step is unchanged and we own the atom, fall back to the
        // previous atom value for any slot not repopulated this cycle. Otherwise, you get a flash of missing content when a memoized marker doesn't re-fire its useLayoutEffect and slotsRef is cleared at the start of the render. When the step key changes, clear all slots not repopulated this cycle to avoid showing stale content from the previous step.
        const preserve = !stepChanged && isOwner;
        return {
          _ownerId: id,
          isOpen: true,
          onClose,
          stepKey: effectiveStepKey,
          modalSize: size,
          className,
          overlayClassName,
          overlayStyle,
          showCloseButton,
          fullHeight,
          headerTitle:
            'headerTitle' in slots
              ? (slots.headerTitle ?? '')
              : preserve
                ? prev.headerTitle
                : '',
          headerContent:
            'headerContent' in slots
              ? slots.headerContent
              : preserve
                ? prev.headerContent
                : undefined,
          headerNoPadding:
            'headerNoPadding' in slots
              ? slots.headerNoPadding
              : preserve
                ? prev.headerNoPadding
                : undefined,
          bodyContent:
            'bodyContent' in slots
              ? slots.bodyContent
              : preserve
                ? prev.bodyContent
                : undefined,
          bodyNoMinHeight:
            'bodyNoMinHeight' in slots
              ? slots.bodyNoMinHeight
              : preserve
                ? prev.bodyNoMinHeight
                : undefined,
          bodyClassName:
            'bodyClassName' in slots
              ? slots.bodyClassName
              : preserve
                ? prev.bodyClassName
                : undefined,
          // Footer slots: never preserve. Footer always re-renders when the
          // composer does via ModalSlotCollectorContext.renderVersion, so
          // footerContent is always written to slotsRef when Footer is in the
          // tree. When Footer is conditionally absent (e.g. PreviewTemplateModalStep
          // hides it during resource/quiz sub-navigation), slotsRef won't have
          // footerContent and the slot is correctly cleared to undefined.
          footerContent:
            'footerContent' in slots ? slots.footerContent : undefined,
          footerClassName:
            'footerClassName' in slots ? slots.footerClassName : undefined,
          primaryButtonText:
            'primaryButtonText' in slots ? slots.primaryButtonText : undefined,
          primaryButtonHandler:
            'primaryButtonHandler' in slots
              ? slots.primaryButtonHandler
              : undefined,
          primaryButtonColor:
            'primaryButtonColor' in slots
              ? slots.primaryButtonColor
              : undefined,
          primaryButtonVariant:
            'primaryButtonVariant' in slots
              ? slots.primaryButtonVariant
              : undefined,
          primaryButtonTestId:
            'primaryButtonTestId' in slots
              ? slots.primaryButtonTestId
              : undefined,
          isDisabled: 'isDisabled' in slots ? slots.isDisabled : undefined,
          isLoading: 'isLoading' in slots ? slots.isLoading : undefined,
          secondaryButtonText:
            'secondaryButtonText' in slots
              ? slots.secondaryButtonText
              : undefined,
          secondaryButtonHandler:
            'secondaryButtonHandler' in slots
              ? slots.secondaryButtonHandler
              : undefined,
          secondaryButtonColor:
            'secondaryButtonColor' in slots
              ? slots.secondaryButtonColor
              : undefined,
          secondaryButtonVariant:
            'secondaryButtonVariant' in slots
              ? slots.secondaryButtonVariant
              : undefined,
          secondaryButtonDisabled:
            'secondaryButtonDisabled' in slots
              ? slots.secondaryButtonDisabled
              : undefined,
          secondaryButtonLoading:
            'secondaryButtonLoading' in slots
              ? slots.secondaryButtonLoading
              : undefined,
          secondaryButtonTestId:
            'secondaryButtonTestId' in slots
              ? slots.secondaryButtonTestId
              : undefined,
          bodyRef,
        };
      });
    } else {
      setModal(prev => {
        if (prev._ownerId !== id) return prev;
        return {
          ...prev,
          isOpen: false,
          _ownerId: undefined,
          showCloseButton: undefined,
        };
      });
    }
  });

  // On unmount, close the atom if this instance still owns it.
  // This handles cases where the parent conditionally unmounts the composer
  // (e.g. `confirmationModalType` set to null by a button handler) without
  // first flipping isOpen to false.
  useEffect(() => {
    const id = modalIdRef.current;
    return () => {
      setModal(prev => {
        if (prev._ownerId !== id) return prev;
        return pathwayModalInitialState;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render children inside the slot-collector context so marker components
  // (Header, Body, Footer) can register their content regardless of nesting
  // depth — including when they are returned from a function component that
  // wraps them in a fragment. Children are only rendered when the modal is
  // open; when closed, the atom retains its last state for the exit animation.
  const slotCollectorContextValue: ModalSlotCollectorContextValue = {
    slotsRef,
    stepKey,
    renderVersion: renderVersion.current,
  };

  return (
    <ModalSlotCollectorContext.Provider value={slotCollectorContextValue}>
      {isOpen ? children : null}
    </ModalSlotCollectorContext.Provider>
  );
};

/**
 * Composable modal that syncs JSX structure to a singleton atom.
 *
 * Renders a slot-collector context provider (no visible DOM output) — place
 * anywhere alongside `<PathwayModalRenderer />`, which must be rendered once
 * higher in the tree.
 *
 * ⚠️ **Single modal per page:** Only one `<PathwayModal>` should be active at a time.
 * If multiple are rendered with `isOpen={true}`, the last one wins; animations won't work.
 * Use a single modal with conditional body content and discrete `stepKey` values instead.
 *
 * @example
 * // ✅ CORRECT: Single modal, conditional body
 * <PathwayModal isOpen={open} onClose={close} stepKey={step}>
 *   <PathwayModal.Header>
 *     <PathwayModal.Title>{step === 'a' ? 'Step A' : 'Step B'}</PathwayModal.Title>
 *   </PathwayModal.Header>
 *   <PathwayModal.Body>
 *     {step === 'a' ? <StepABody /> : <StepBBody />}
 *   </PathwayModal.Body>
 * </PathwayModal>
 *
 * @example
 * // ❌ WRONG: Multiple modals with competing stepKeys
 * <PathwayModal isOpen={open1} stepKey="modal1">...</PathwayModal>
 * <PathwayModal isOpen={open2} stepKey="modal2">...</PathwayModal>
 */
// eslint-disable-next-line react-refresh/only-export-components -- this component is a pure data carrier, not a visual component
export const PathwayModal = Object.assign(PathwayModalComposer, {
  Title,
  Header,
  Body,
  Footer,
  PrimaryButton,
  SecondaryButton,
  Step,
});

/**
 * Ownership model & race conditions
 * ----------------------------------
 * The atom holds a single `_ownerId` — the `useId()` ref of whichever composer
 * last wrote `isOpen: true`. Every write from a closed composer is guarded by a
 * functional update that bails out if `prev._ownerId !== id`, so a mounted-but-
 * closed sibling can never clobber a different modal that is currently open.
 *
 * Known race windows:
 *
 * 1. Fast close → open (< 300 ms):
 *    `handleOpenChange` schedules `resetModal` 300 ms after the overlay starts
 *    exiting (to let the exit animation finish). If a new composer takes
 *    ownership within that window, the `setTimeout` callback uses a functional
 *    update (`prev.isOpen ? prev : reset`) and skips the reset, so the new
 *    modal is safe.
 *
 * 2. Two composers open simultaneously:
 *    The last render wins — the atom only holds one state snapshot. The dev-
 *    mode console warning fires immediately when a second owner writes
 *    `isOpen: true` while `__DEV_activeModalsRef` already has an entry.
 *
 * 3. Unmount without close (e.g. route change):
 *    If the composer unmounts while `isOpen` is still true, the cleanup effect
 *    resets the atom to `pathwayModalInitialState`. Because `PathwayModalRenderer`
 *    lives above the route boundary in `LayoutWrapper`, it stays mounted and
 *    React Aria's normal `data-exiting` animation still plays. This is the
 *    correct behavior for route changes — the modal cleans itself up without
 *    any manual intervention from the parent. But this doesn't really matter for
 *    development. It's just something to be aware of.
 */

export default PathwayModal;

// ---------------------------------------------------------------------------
// PathwayModalRenderer (singleton) — reads atom, renders the actual modal UI
// ---------------------------------------------------------------------------

type AnimationState = 'idle' | 'resizing';

export const PathwayModalRenderer: React.FC = () => {
  const atomState = useAtomValue(pathwayModalAtom);
  const resetModal = useSetAtom(pathwayModalAtom);

  const bodyClassName = atomState.bodyClassName;
  const bodyContent = atomState.bodyContent;
  const bodyNoMinHeight = atomState.bodyNoMinHeight;
  const className = atomState.className;
  const footerContent = atomState.footerContent;
  const footerClassName = atomState.footerClassName;
  const headerContent = atomState.headerContent;
  const headerNoPadding = atomState.headerNoPadding ?? false;
  const headerTitle = atomState.headerTitle;
  const isDisabled = atomState.isDisabled;
  const isError = atomState.isError;
  const isLoading = atomState.isLoading;
  const isOpen = atomState.isOpen ?? false;
  const modalSize = atomState.modalSize ?? 'md';
  const fullHeight = atomState.fullHeight ?? false;
  const onClose = atomState.onClose;
  const overlayClassName = atomState.overlayClassName;
  const overlayStyle = atomState.overlayStyle;
  const primaryButtonColor = atomState.primaryButtonColor ?? 'secondary';
  const primaryButtonHandler = atomState.primaryButtonHandler;
  const primaryButtonTestId = atomState.primaryButtonTestId;
  const primaryButtonText = atomState.primaryButtonText;
  const primaryButtonVariant = atomState.primaryButtonVariant ?? 'filled';
  const secondaryButtonColor = atomState.secondaryButtonColor ?? 'secondary';
  const secondaryButtonDisabled = atomState.secondaryButtonDisabled;
  const secondaryButtonHandler = atomState.secondaryButtonHandler;
  const secondaryButtonLoading = atomState.secondaryButtonLoading;
  const secondaryButtonTestId = atomState.secondaryButtonTestId;
  const secondaryButtonText = atomState.secondaryButtonText;
  const secondaryButtonVariant = atomState.secondaryButtonVariant ?? 'outlined';
  const showCloseButton = atomState.showCloseButton ?? true;
  const externalBodyRef = atomState.bodyRef;

  const modalBodyRef = useRef<HTMLDivElement>(null);

  // Merged ref: populates both the internal modalBodyRef (used for animations
  // and scroll tracking) and the caller-supplied bodyRef forwarded through the
  // atom (e.g. for useScrollMemory).
  const mergedBodyRef = useCallback(
    (node: HTMLDivElement | null) => {
      modalBodyRef.current = node;
      if (externalBodyRef) {
        externalBodyRef.current = node;
      }
    },
    [externalBodyRef],
  );
  const [animationState, setAnimationState] = useState<AnimationState>('idle');
  const [contentHeight, setContentHeight] = useState<number | 'auto'>('auto');

  const runIdRef = useRef(0);
  const animationStateRef = useRef<AnimationState>('idle');
  const lastIdleHeightRef = useRef(0);
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  useEffect(() => {
    if (!isOpen) {
      // Reset height tracking so a re-open with different content doesn't
      // compare against a stale height and trigger a spurious resize animation.
      animationStateRef.current = 'idle';
      lastIdleHeightRef.current = 0;
      setAnimationState('idle');
      setContentHeight('auto');
    }
  }, [isOpen]);

  const handleAnimation = async (state: AnimationState) => {
    if (state !== 'resizing') return;

    const runId = ++runIdRef.current;
    const stillLatest = () => runId === runIdRef.current;

    if (modalBodyRef.current) {
      const el = modalBodyRef.current;
      const original = el.style.height;

      el.style.height = 'auto';
      const newHeight = el.offsetHeight;
      el.style.height = original;
      // force reflow so the next height change transitions reliably
      void el.offsetHeight;

      setContentHeight(newHeight);
    }

    await sleep(STEP_DURATION);
    if (!stillLatest()) return;

    animationStateRef.current = 'idle';
    setContentHeight('auto');
    setAnimationState('idle');
  };

  // Pre-paint intercept: detect meaningful height changes while idle and
  // animate the resize before the browser paints. Uses DOM height comparison
  // rather than content reference equality — JSX always produces new object
  // references on every render, so reference comparison fires on every
  // re-render (e.g. every isFetching toggle) causing rapid animation cycling
  // and header flickering. DOM offsetHeight comparison fires only when the
  // rendered height actually changes — stable across re-renders that produce
  // the same visual output (e.g. spinner staying at minHeight).
  //
  // When triggered:
  //   • Locks contentHeight to the previous idle height so CSS can transition.
  //   • Header and body render atom values directly so current content stays
  //     visible during the height animation.
  //   • Resets contentHeight to 'auto' and returns to idle after the transition.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (
      fullHeight ||
      animationStateRef.current !== 'idle' ||
      !isOpen ||
      !modalBodyRef.current
    )
      return;
    const currentHeight = modalBodyRef.current.offsetHeight;
    const prevHeight = lastIdleHeightRef.current;
    lastIdleHeightRef.current = currentHeight;
    if (prevHeight > 0 && Math.abs(currentHeight - prevHeight) > 2) {
      setContentHeight(prevHeight);
      animationStateRef.current = 'resizing';
      setAnimationState('resizing');
    }
  });

  useLayoutEffect(() => {
    runIdRef.current++;
    handleAnimation(animationState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationState]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose?.();
      // Reset atom after the overlay's 300ms exit animation. Use a functional
      // update so we only reset if no new modal has taken ownership in the
      // meantime — prevents clobbering a modal that opens within 300ms.
      setTimeout(() => {
        resetModal(prev => (prev.isOpen ? prev : pathwayModalInitialState));
      }, 300);
    }
  };
  return (
    <Modal.Overlay
      className={cn('z-2000', overlayClassName)}
      data-testid="PathwayModal"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={showCloseButton}
      style={overlayStyle}
    >
      <Modal.Dialog
        isCloseButtonHidden={!showCloseButton}
        size={modalSize}
        className={cn(
          'relative! mx-4! flex! max-h-[90vh]! w-full! max-w-[610px]! flex-col! overflow-y-hidden! 2xl:max-w-[840px]! [&>section]:flex! [&>section]:min-h-0! [&>section]:flex-col!',
          fullHeight && 'h-[90dvh]! [&>section]:h-full!',
          className,
        )}
      >
        {(headerTitle || headerContent) && (
          <ScrollModalHeader
            scrollRef={modalBodyRef}
            className={cn(
              !headerNoPadding && 'px-4! md:px-8!',
              !showCloseButton &&
                // Matches @cfa/react-core Modal.Dialog close-button header row height
                'pt-[calc(var(--cfa-size-spacing-md)*2+1.5rem)]!',
            )}
            headerTitle={headerTitle}
          >
            {headerContent}
          </ScrollModalHeader>
        )}
        <div
          ref={mergedBodyRef}
          style={
            fullHeight
              ? { overflow: 'auto', opacity: 1 }
              : {
                  height:
                    contentHeight === 'auto' ? 'auto' : `${contentHeight}px`,
                  overflow: contentHeight === 'auto' ? 'visible' : 'hidden',
                  opacity: animationState === 'resizing' ? 0 : 1,
                  transition: `height ${STEP_DURATION}ms ease-out`,
                }
          }
          className={cn(
            'flex-auto! overflow-y-auto!',
            !fullHeight &&
              animationState !== 'idle' &&
              !bodyNoMinHeight &&
              'min-h-[360px]!',
          )}
        >
          <Modal.Content
            className={cn(
              'm-0! w-full! max-w-none! justify-start! px-4! md:px-8!',
              bodyClassName,
            )}
          >
            {bodyContent && <div className="mx-auto w-full">{bodyContent}</div>}
          </Modal.Content>
        </div>
        {!!(footerContent ?? primaryButtonText ?? secondaryButtonText) && (
          <ScrollModalFooter
            scrollRef={modalBodyRef}
            className={footerClassName}
          >
            {footerContent ?? (
              <div className="md:wrap-reverse flex w-full flex-col-reverse justify-center gap-4 md:flex-row md:flex-wrap">
                {secondaryButtonHandler && secondaryButtonText && (
                  <Button
                    className="md:flex-1"
                    color={secondaryButtonColor}
                    data-testid={
                      secondaryButtonTestId ?? 'PathwayModalSecondaryButton'
                    }
                    isDisabled={secondaryButtonDisabled}
                    isPending={secondaryButtonLoading}
                    onPress={secondaryButtonHandler}
                    variant={secondaryButtonVariant}
                  >
                    {secondaryButtonText}
                  </Button>
                )}
                {primaryButtonText && (
                  <Button
                    className={cn(
                      secondaryButtonHandler &&
                        secondaryButtonText &&
                        'md:flex-1',
                    )}
                    color={primaryButtonColor}
                    data-testid={
                      primaryButtonTestId ?? 'ConfirmationPopUpPrimaryButton'
                    }
                    isDisabled={isDisabled}
                    isPending={isLoading}
                    onPress={primaryButtonHandler}
                    variant={isError ? 'destructive' : primaryButtonVariant}
                  >
                    {primaryButtonText}
                  </Button>
                )}
              </div>
            )}
          </ScrollModalFooter>
        )}
      </Modal.Dialog>
    </Modal.Overlay>
  );
};
